import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE;

export default function Renewal({ userid, user }) {
  const isAdmin = user?.user_role === 'ADMIN';
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    id: null,
    userid: null,
    name: '',
    gender: 'Male',
    mobile: '',
    whatsapp_no: '',
    aadhar: '',
    epic_no: '',
    card_no: '',
    vidhansabha: 'Karad South',
    village: '',
    booth_no: '',
    gan_name: '',
    zp_name: '',
    reference: '',
    form_owner: '',
    taluka: '',
    district: '',
    form_date: '',
    ack_no: '',
    wfc_date: '',
    wfc_location: 'Karad',
    wfc_other_location: '',
    wfc_remarks: '',
    operator: '',
    payment_status: 'UNPAID',
    cash_amount: '',
    online_amount: '',
    payment_date: '',
    form_status: 'Submitted',
    next_renewal_date: '',
  });
  const [editing, setEditing] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    reference: '',
    form_owner: '',
    form_date_from: '',
    form_date_to: '',
    wfc_date_from: '',
    wfc_date_to: '',
    next_renewal_date_from: '',
    next_renewal_date_to: '',
    payment_date_from: '',
    payment_date_to: '',
    form_status: '',
    payment_status: '',
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // fetch records on mount
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchRecords() {
    try {
      const res = await fetch(`${API_BASE}/renewal-records`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRecords((data || []).map(mapFromApi));
    } catch (err) {
      console.error('fetchRecords:', err);
    }
  }

  function mapFromApi(a) {
    const toInputDate = (d) => (d ? d.slice(0, 10) : '');
    return {
      ...a,
      id: a.id,
      name: a.name || '',
      gender: a.gender || 'Male',
      mobile: a.mobile || '',
      whatsapp_no: a.whatsapp_no || '',
      aadhar: a.aadhar || '',
      epic_no: a.epic_no || '',
      card_no: a.card_no || '',
      vidhansabha: a.vidhansabha || 'Karad South',
      village: a.village || '',
      booth_no: a.booth_no || '',
      gan_name: a.gan_name || '',
      zp_name: a.zp_name || '',
      reference: a.reference || '',
      form_owner: a.form_owner || '',
      taluka: a.taluka || '',
      district: a.district || '',
      form_date: toInputDate(a.form_date),
      ack_no: a.ack_no || '',
      wfc_date: toInputDate(a.wfc_date),
      wfc_location: a.wfc_location || 'Karad',
      wfc_other_location: a.wfc_other_location || '',
      wfc_remarks: a.wfc_remarks || '',
      operator: a.operator || '',
      payment_status: a.payment_status || 'UNPAID',
      cash_amount: a.cash_amount || '',
      online_amount: a.online_amount || '',
      payment_date: toInputDate(a.payment_date),
      form_status: (a.form_status === 'RECEIVED' ? 'Received' :
        (a.form_status === 'UNRECEIVED' ? 'Unreceived' : (a.form_status || 'Submitted'))),
      next_renewal_date: toInputDate(a.next_renewal_date),
    };
  }

  function mapToApi(f, uid) {
    const data = { ...f, userid: uid };
    // Convert empty strings to null for database compatibility
    ['form_date', 'wfc_date', 'next_renewal_date', 'payment_date', 'cash_amount', 'online_amount'].forEach(key => {
      if (data[key] === '') data[key] = null;
    });
    return data;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((s) => {
      let next = { ...s, [name]: value };
      if (name === 'wfc_location' && value !== 'Other') {
        next.wfc_other_location = '';
      }
      return next;
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (saving) return; // Prevent double submission
    setSaving(true);
    (async () => {
      try {
        if (editing) {
          const payload = mapToApi(form, userid);
          payload._user_role = user?.user_role || '';
          const res = await fetch(`${API_BASE}/renewal-records/${form.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error('Update failed');
        } else {
          const res = await fetch(`${API_BASE}/renewal-records`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mapToApi(form, userid)),
          });
          if (!res.ok) throw new Error('Create failed');
        }
        await fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } catch (err) {
        console.error('handleSubmit:', err);
        alert('Save failed');
      } finally {
        setSaving(false);
      }
    })();
  }

  function handleEdit(id) {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }
    setForm({ ...rec });
    setEditing(true);
    setDrawerOpen(true);
  }

  async function handleToggleLock(id) {
    try {
      const res = await fetch(`${API_BASE}/renewal-records/${id}/toggle-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ admin_name: user?.namename || 'admin' })
      });
      if (!res.ok) throw new Error('Toggle lock failed');
      await fetchRecords();
    } catch (err) {
      alert('Error toggling lock: ' + err.message);
    }
  }

  function handleDelete(id) {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }

    if (!window.confirm('Delete this record?')) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/renewal-records/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Delete failed');
        await fetchRecords();
      } catch (err) {
        console.error('handleDelete:', err);
        alert('Delete failed');
      }
    })();
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const filteredRecords = React.useMemo(() => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    return records.filter((r) => {
      if (!m(r.name).includes(m(filters.name))) return false;
      if (!m(r.mobile).includes(m(filters.mobile))) return false;
      if (!m(r.aadhar).includes(m(filters.aadhar))) return false;
      if (!m(r.reference).includes(m(filters.reference))) return false;
      if (!m(r.form_owner).includes(m(filters.form_owner))) return false;
      if (filters.form_date_from && r.form_date < filters.form_date_from) return false;
      if (filters.form_date_to && r.form_date > filters.form_date_to) return false;
      if (filters.wfc_date_from && r.wfc_date < filters.wfc_date_from) return false;
      if (filters.wfc_date_to && r.wfc_date > filters.wfc_date_to) return false;
      if (filters.next_renewal_date_from && r.next_renewal_date < filters.next_renewal_date_from) return false;
      if (filters.next_renewal_date_to && r.next_renewal_date > filters.next_renewal_date_to) return false;
      if (filters.payment_date_from && r.payment_date < filters.payment_date_from) return false;
      if (filters.payment_date_to && r.payment_date > filters.payment_date_to) return false;
      if (filters.form_status && r.form_status !== filters.form_status) return false;
      if (filters.payment_status && r.payment_status !== filters.payment_status) return false;
      return true;
    });
  }, [records, filters]);

  function resetForm() {
    setForm({
      id: null,
      userid: null,
      name: '',
      gender: 'Male',
      mobile: '',
      whatsapp_no: '',
      aadhar: '',
      epic_no: '',
      card_no: '',
      vidhansabha: 'Karad South',
      village: '',
      booth_no: '',
      gan_name: '',
      zp_name: '',
      reference: '',
      form_owner: '',
      taluka: '',
      district: '',
      form_date: '',
      ack_no: '',
      wfc_date: '',
      wfc_location: 'Karad',
      wfc_other_location: '',
      wfc_remarks: '',
      operator: '',
      payment_status: 'UNPAID',
      cash_amount: '',
      online_amount: '',
      payment_date: '',
      form_status: 'Submitted',
      next_renewal_date: '',
    });
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>Renewal Form Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Renewal
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Name', 'Mobile', 'Aadhar', 'Reference', 'Form Owner', 'Form Date', 'WFC Date', 'Next Renewal', 'Form Status', 'Pay Status', 'Pay Date', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="reference" value={filters.reference} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="form_owner" value={filters.form_owner} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField size="small" variant="standard" name="form_date_from" type="date" value={filters.form_date_from} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="From" />
                    <TextField size="small" variant="standard" name="form_date_to" type="date" value={filters.form_date_to} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="To" />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField size="small" variant="standard" name="wfc_date_from" type="date" value={filters.wfc_date_from} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="From" />
                    <TextField size="small" variant="standard" name="wfc_date_to" type="date" value={filters.wfc_date_to} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="To" />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField size="small" variant="standard" name="next_renewal_date_from" type="date" value={filters.next_renewal_date_from} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="From" />
                    <TextField size="small" variant="standard" name="next_renewal_date_to" type="date" value={filters.next_renewal_date_to} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="To" />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Select size="small" variant="standard" name="form_status" value={filters.form_status || ''} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', minWidth: 100 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Submitted">Submitted</MenuItem>
                    <MenuItem value="Accepted">Accepted</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                    <MenuItem value="Re-submitted">Re-submitted</MenuItem>
                    <MenuItem value="Received">Received</MenuItem>
                    <MenuItem value="Unreceived">Unreceived</MenuItem>
                  </Select>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Select size="small" variant="standard" name="payment_status" value={filters.payment_status || ''} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', minWidth: 80 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="PAID">PAID</MenuItem>
                    <MenuItem value="UNPAID">UNPAID</MenuItem>
                  </Select>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <TextField size="small" variant="standard" name="payment_date_from" type="date" value={filters.payment_date_from} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="From" />
                    <TextField size="small" variant="standard" name="payment_date_to" type="date" value={filters.payment_date_to} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black', width: '100%' }} InputProps={{ disableUnderline: true }} InputLabelProps={{ shrink: true }} label="To" />
                  </Box>
                </TableCell>
                <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((r) => (
                <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                  <TableCell>{r.mobile}</TableCell>
                  <TableCell>{r.aadhar}</TableCell>
                  <TableCell>{r.reference}</TableCell>
                  <TableCell>{r.form_owner}</TableCell>
                  <TableCell>{formatDate(r.form_date)}</TableCell>
                  <TableCell>{formatDate(r.wfc_date)}</TableCell>
                  <TableCell>{formatDate(r.next_renewal_date)}</TableCell>
                  <TableCell>{r.form_status}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.payment_status}
                      size="small"
                      color={r.payment_status === 'PAID' ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(r.payment_date)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {isAdmin && (
                        <IconButton size="small" onClick={() => handleToggleLock(r.id)}
                          title={r.unlock_granted_by ? `Unlock granted — click to re-lock` : 'Click to unlock for editing'}
                          sx={{ bgcolor: r.unlock_granted_by ? '#fff3e0' : '#fce4ec', '&:hover': { bgcolor: r.unlock_granted_by ? '#ffe0b2' : '#f8bbd0' } }}>
                          {r.unlock_granted_by ? <LockOpenIcon fontSize="small" color="warning" /> : <LockIcon fontSize="small" color="error" />}
                        </IconButton>
                      )}
                      <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)}
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Edit'}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1 }}>
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} 
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Delete'}
                        sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' }, opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1 }}>
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Renewal' : 'Add New Renewal'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Worker's Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Whatsapp Mobile Number" name="whatsapp_no" value={form.whatsapp_no} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Village" name="village" value={form.village} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Taluka" name="taluka" value={form.taluka} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="District" name="district" value={form.district} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="MH No" name="card_no" value={form.card_no} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth variant="outlined">
              <InputLabel id="form-owner-label">Form owner</InputLabel>
              <Select labelId="form-owner-label" label="Form owner" name="form_owner" value={form.form_owner} onChange={handleChange}>
                <MenuItem value="NITIN SIR">NITIN SIR</MenuItem>
                <MenuItem value="KUMBHAR MADAM">KUMBHAR MADAM</MenuItem>
                <MenuItem value="KAMBALE MADAM">KAMBALE MADAM</MenuItem>
                <MenuItem value="TAMBAVE OFFICE">TAMBAVE OFFICE</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Reference" name="reference" value={form.reference} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="Form Online Date" name="form_date" type="date" InputLabelProps={{ shrink: true }} value={form.form_date} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="WFC Visit Date" name="wfc_date" type="date" InputLabelProps={{ shrink: true }} value={form.wfc_date} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Acknowledgement No." name="ack_no" value={form.ack_no} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="wfc-loc-label">WFC Visit Location</InputLabel>
              <Select labelId="wfc-loc-label" label="WFC Visit Location" name="wfc_location" value={form.wfc_location} onChange={handleChange}>
                <MenuItem value="Karad">Karad</MenuItem>
                <MenuItem value="North Karad">North Karad</MenuItem>
                <MenuItem value="Patan">Patan</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.wfc_location === 'Other' && (
              <TextField label="Other WFC Location" name="wfc_other_location" value={form.wfc_other_location} onChange={handleChange} fullWidth variant="outlined" />
            )}

            <TextField label="WFC Visit Remarks" name="wfc_remarks" value={form.wfc_remarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />

            <TextField label="Operator Name" name="operator" value={form.operator} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="form-status-label">Form Status</InputLabel>
              <Select labelId="form-status-label" label="Form Status" name="form_status" value={form.form_status} onChange={handleChange}>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
                <MenuItem value="Re-submitted">Re-submitted</MenuItem>
                <MenuItem value="Received">Received</MenuItem>
                <MenuItem value="Unreceived">Unreceived</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Next Renewal Date" name="next_renewal_date" type="date" InputLabelProps={{ shrink: true }} value={form.next_renewal_date} onChange={handleChange} fullWidth variant="outlined" />

            <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, bgcolor: '#f9f9f9' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#0b4a6f', fontWeight: 'bold' }}>Payment Details</Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="payment-status-label">Payment Status</InputLabel>
                  <Select labelId="payment-status-label" label="Payment Status" name="payment_status" value={form.payment_status} onChange={handleChange}>
                    <MenuItem value="PAID">PAID</MenuItem>
                    <MenuItem value="UNPAID">UNPAID</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Cash Amount Rs."
                    name="cash_amount"
                    type="number"
                    value={form.cash_amount}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Online Amount Rs."
                    name="online_amount"
                    type="number"
                    value={form.online_amount}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Stack>
                <TextField label="Payment Date" name="payment_date" type="date" InputLabelProps={{ shrink: true }} value={form.payment_date} onChange={handleChange} fullWidth variant="outlined" />
              </Stack>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }} disabled={saving}>
              {saving ? 'Saving...' : (editing ? 'Update' : 'Save')}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
